import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "@phosphor-icons/react";
import { listBlogTeasers, DESTINATION_AIRPORT } from "@/data/blogs";

const FEATURED_COUNT = 3;

function PostCard({ post }) {
  return (
    // h-full + flex-col so all cards in the row stretch to the same height
    // (the grid uses stretch by default); inside we push the "Read the guide"
    // link to the bottom with mt-auto so it aligns across every card.
    <article className="bg-white border border-[#E0DDD6] overflow-hidden lift h-full w-full flex flex-col" data-testid={`blog-post-${post.slug}`}>
      <Link to={`/blog/${post.slug}`} className="block group">
        <div className="aspect-[16/10] overflow-hidden">
          <img
            src={post.hero}
            alt={post.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
        </div>
      </Link>
      <div className="p-5 md:p-6 flex-1 flex flex-col">
        <p className="tcp-overline text-[#C29B62] mb-2">
          {post.read_minutes} min read · {post.published_at}
        </p>
        {/* Title clamped to 2 lines so the italic subtitle sits at the same
            baseline across every card in the row. */}
        <Link to={`/blog/${post.slug}`} className="block hover:text-[#C29B62] transition-colors">
          <h3 className="font-serif text-2xl md:text-3xl tracking-tight leading-tight text-[#1C1C1C] line-clamp-2 min-h-[3.6rem] md:min-h-[4.5rem]">
            {post.title}
          </h3>
        </Link>
        <p className="font-serif italic text-base text-[#666460] mt-1.5 line-clamp-1">{post.subtitle}</p>
        <p className="text-sm text-[#666460] mt-3 leading-relaxed line-clamp-2">{post.excerpt}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {post.destinations.map((d) => {
            const airport = DESTINATION_AIRPORT[d.anchor];
            const href = airport ? `/destinations/${airport.code}` : `/blog/${post.slug}#${d.anchor}`;
            return (
              <Link
                key={d.anchor}
                to={href}
                data-testid={`blog-chip-${post.slug}-${d.anchor}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs tracking-[0.16em] uppercase border border-[#E0DDD6] text-[#666460] hover:border-[#C29B62] hover:text-[#C29B62] transition-colors"
              >
                <MapPin size={12} weight="fill" /> {d.name}
              </Link>
            );
          })}
        </div>
        <Link
          to={`/blog/${post.slug}`}
          data-testid={`blog-read-${post.slug}`}
          className="mt-auto pt-4 inline-flex items-center gap-2 text-xs tracking-[0.16em] uppercase text-[#C29B62] hover:text-[#A88550]"
        >
          Read the guide <ArrowRight size={14} />
        </Link>
      </div>
    </article>
  );
}

export default function Blog() {
  const allPosts = listBlogTeasers();
  const featured = allPosts.slice(0, FEATURED_COUNT);
  const hasMore = allPosts.length > FEATURED_COUNT;

  return (
    <section className="bg-[#F7F5F0] py-10 md:py-20 px-5 md:px-12 lg:px-24" data-testid="blog-section">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 md:gap-6 mb-5 md:mb-8">
        <div className="max-w-2xl">
          <p className="tcp-overline text-[#C29B62] mb-2 md:mb-3">Travel Stories</p>
          <h2 className="font-serif text-3xl sm:text-5xl lg:text-6xl tracking-tighter leading-tight md:leading-none text-[#1C1C1C]">
            Where to <em className="italic">next?</em>
          </h2>
          <p className="text-[#666460] text-sm md:text-base mt-2 md:mt-4 max-w-md leading-relaxed">
            Editor-curated guides from our travel desk — the destinations our customers ask
            about most, with the experiences worth the flight.
          </p>
        </div>
        {hasMore && (
          <Link
            to="/journal"
            data-testid="blog-view-all"
            className="self-start md:self-end inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-[#C29B62] hover:text-[#A88550] border-b border-[#C29B62] hover:border-[#A88550] pb-1 transition-colors"
          >
            View all {allPosts.length} stories <ArrowRight size={14} />
          </Link>
        )}
      </div>

      {/* Horizontal swipe carousel on mobile · 3-col grid on desktop. */}
      <div
        className="
          flex md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8
          overflow-x-auto md:overflow-visible no-scrollbar
          snap-x snap-mandatory md:snap-none
          -mx-5 md:mx-0 px-5 md:px-0 pb-3 md:pb-0
        "
        data-testid="blog-track"
      >
        {featured.map((p) => (
          // Wrapper needs h-full + flex so each carousel cell stretches to
          // the tallest sibling on mobile (desktop grid stretches by default).
          <div key={p.slug} className="w-[85%] md:w-auto shrink-0 md:shrink snap-start flex">
            <PostCard post={p} />
          </div>
        ))}
      </div>

      {/* Mobile-only swipe hint */}
      <p className="md:hidden mt-3 text-[10px] tracking-[0.22em] uppercase text-[#A09E9A] text-center">
        Swipe →
      </p>
    </section>
  );
}
